import * as React from 'react';
import {
    CARD_ESTIMATE_ESTIMATE_TOOLTIP,
    CARD_ESTIMATE_EXAMPLE,
    CARD_ESTIMATE_PLACEHOLDER,
    CARD_ESTIMATE_REMOVE_TOOLTIP,
} from '../CardEstimateAndSpentTimeSection/constants';
import { ICardEstimateProps } from './types';
import { Icon, Input } from 'kui';
import { ESettingsSectionType } from '../../../../../base/components/SettingsSection/types';
import { CLASS_CARD_DETAILS } from '../../../constants';
import { getAsidePanelTooltip } from '../../../../asidePanel/helpers/getAsidePanelTooltip';
import { parseEstimate } from '../CardEstimateAndSpentTimeSection/behaviour/parseEstimate';

export function CardEstimate ({
    estimateStr,
    isRequired,
    sectionType,
    workHoursInDay,
    onEstimateChange,
    onError
}: ICardEstimateProps) {
    const isPopup = sectionType === ESettingsSectionType.POPUP;
    const isReadonly = sectionType === ESettingsSectionType.READONLY;

    const className = CLASS_CARD_DETAILS + '-times__estimate';
    const classWrapper = className + '-wrapper';

    const labelRef = React.useRef(null);

    const [isFocus, setFocus] = React.useState(null);
    const [inputState, setInputState] = React.useState(null);
    const [showValid, setShowValid] = React.useState(null);
    const [estimateHook, setEstimateHook] = React.useState(estimateStr);

    const getSyntheticEventTargetValue = (
        e: React.SyntheticEvent,
    ): string => {
        const target = e.target as HTMLInputElement;
        return target.value || ''
    };

    const isEventTargetValueValid = (
        e: React.SyntheticEvent,
    ): boolean => {
        const targetValue = getSyntheticEventTargetValue(e);
        const isValid = targetValue.trim().length === 0 || parseEstimate(targetValue, workHoursInDay) > 0;
        return isRequired ? targetValue.length > 0 && isValid : isValid;
    };

    const handleChange = (
        e: React.SyntheticEvent,
    ) => {
        setEstimateHook(getSyntheticEventTargetValue(e));
        if (showValid) {
            const isValid = isEventTargetValueValid(e);
            const inputStateStr = isValid ? 'success' : 'error';
            setInputState(inputStateStr);
        } else {
            setInputState(null);
        }
    };

    function onKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Escape') {
            event.stopPropagation(); // to avoid the aside-panel closure on esc if input is in focus
            setEstimateHook(estimateStr);
            blurHandle();
        }
    }

    function blurHandle() {
        if (!labelRef.current) return;

        const input = labelRef.current.getInput();
        if (!input) return;

        input.closest(`[tabindex]`).focus();
    }

    const handleEnter = (
        e: React.SyntheticEvent,
        blur?: any, // on Enter
    ) => {
        const targetValue = getSyntheticEventTargetValue(e);
        const isValid = isEventTargetValueValid(e);
        setShowValid(!isValid);
        if (!isValid) {
            setInputState('error');
        } else {
            setFocus(false);
            setInputState(null);
            const estimateInMinutes = parseEstimate(targetValue, workHoursInDay);
            onEstimateChange(estimateInMinutes);
            if (blur) blur();
        }
    };

    React.useEffect(() => {
        if (inputState === 'error') {
            onError();
        }
    }, [inputState]);

    React.useEffect(() => {
        setEstimateHook(estimateStr);
    }, [estimateStr]);

    return (
        <div
            className={`
                ${classWrapper}
                ${isFocus ? classWrapper + '--focus' : ''}
                ${!!estimateStr ? classWrapper + '--filled' : ''}
            `}
        >
            <Icon className={className + '-icon'} xlink={'plus'} size={24} />
            <Input
                autoFocus={isPopup}
                className={`
                    ${className}
                    ${isRequired ? className + '--required' : ''}
                    ${!estimateStr || !estimateStr.length ? className + '--error' : ''}
                `}
                isClearable
                iconTooltip={
                    getAsidePanelTooltip({
                        direction: 'up-left',
                        value: CARD_ESTIMATE_REMOVE_TOOLTIP,
                    })
                }
                tooltip={
                    getAsidePanelTooltip({
                        direction: 'up',
                        value: CARD_ESTIMATE_ESTIMATE_TOOLTIP,
                    })
                }
                readOnly={isReadonly}
                autosize={false}
                state={inputState}
                onFocus={() => setFocus(true)}
                ref={labelRef}
                placeholder={isFocus ?  CARD_ESTIMATE_EXAMPLE : CARD_ESTIMATE_PLACEHOLDER}
                onChange={handleChange}
                onEnter={(e) => handleEnter(e, blurHandle) }
                onBlur={handleEnter}
                value={estimateHook}
                onKeyDown = {onKeyDown}
            />
        </div>
    );
}
